/**
 * Streamdown component map for an Evidence sub-agent answer: the shared
 * evidence markdown components, the `<cite-badge>` and `<img-figure>` handlers,
 * and the three custom callout blocks (`keypoints` / `safetycallout` /
 * `keysafetynotes`).
 * Each callout re-parses its children as markdown and resolves cite-badge
 * markers via the same `citationMapRef`, so citations look identical in prose
 * or inside a callout.
 */

import { useMemo, type ComponentProps, type ReactNode, type RefObject } from 'react';
import type { Streamdown } from 'streamdown';

import { getEvidenceMdComponents } from '@/components/chat/components/markdown-components';
import {
  useIsMutedKeySafetyNotesEnabled,
  useIsPartnerNejmFiguresEnabled,
} from '@/features/flags';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import {
  densityToCalloutVariant,
  responseParagraphClasses,
  type ProseDensity,
} from '@heidi/answer-kit';
import {
  CitationBadge,
  EvidenceImageBlock,
  KeyPointsBlock,
  KeySafetyNotes,
  PartnerFigurePreview,
  SafetyCallout,
} from '@heidi/evidence';
import { shouldMuteKeySafetyNotes, type SimpleCitation } from '@heidi/evidence/contracts';
import { cn } from '@heidi/utils';
import type { EvidenceAgentImage } from '../citation-tokens/citation-model';

type StreamdownComponents = ComponentProps<typeof Streamdown>['components'];

interface UseEvidenceAnswerComponentsParams {
  /** Latest `data-idx → SimpleCitation[]` map, read via a ref so handler identity stays stable. */
  citationMapRef: RefObject<Map<number, SimpleCitation[]>>;
  /** Latest `image_id → image` map for `<img-figure>` anchors; same ref rationale. */
  imageMapRef: RefObject<Map<string, EvidenceAgentImage> | undefined>;
  streaming: boolean;
  /**
   * Prose density for the column this answer renders into. Callouts follow it,
   * so the type matches the prose around them rather than the session state.
   */
  density: ProseDensity;
  /** `null` in Projects (no scribe session) — forwarded to `CitationBadge` for analytics. */
  sessionId: string | null;
  /**
   * Bumps once the partner-integrations cache resolves after first paint. Folded
   * into the `cite-badge` element key: `CitationBadge` memoizes on `sources[i].url`
   * + analytics ids only, so a late-resolving cache warm never changes those props
   * and the badge would otherwise keep its favicon-fallback render forever.
   */
  partnerConfigVersion?: number;
}

export const useEvidenceAnswerComponents = ({
  citationMapRef,
  imageMapRef,
  streaming,
  density,
  sessionId,
  partnerConfigVersion,
}: UseEvidenceAnswerComponentsParams): StreamdownComponents => {
  const partnerNejmFiguresEnabled = useIsPartnerNejmFiguresEnabled();
  const isMutedKeySafetyNotesEnabled = useIsMutedKeySafetyNotesEnabled();
  const kindeRegion = useIsUKOrEU();
  const isKeySafetyNotesMuted = shouldMuteKeySafetyNotes({
    flagEnabled: isMutedKeySafetyNotesEnabled,
    region: kindeRegion.region,
    isPending: kindeRegion.isPending,
    isError: kindeRegion.isError,
  });
  return useMemo(() => {
    // Each callout's `<cite-badge>` renderer: the surface_block, partner fill
    // and className are per-surface and DOM-visible, and the citation map is
    // this hook's.
    const keyPointsCiteBadge = ({ 'data-idx': dataIdx }: { 'data-idx'?: string }) => {
      const idx = Number(dataIdx);
      const sources = citationMapRef.current?.get(idx);
      if (!sources?.length) return null;
      return (
        <CitationBadge
          sources={sources}
          sessionId={sessionId ?? undefined}
          // EVI-1486 round 4: key_points surface_block + 1-based citation
          // ordinal so prose vs. summary clicks are distinguishable.
          surfaceBlock="key_points"
          citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
          // Info-toned chrome so the badge stays legible against the
          // blue card; details live in CitationBadge.
          surfaceTheme="key-points"
        />
      );
    };

    const safetyCalloutCiteBadge = ({ 'data-idx': dataIdx }: { 'data-idx'?: string }) => {
      const idx = Number(dataIdx);
      const sources = citationMapRef.current?.get(idx);
      if (!sources?.length) return null;
      return (
        <CitationBadge
          sources={sources}
          sessionId={sessionId ?? undefined}
          surfaceBlock="safety_callout"
          citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
          className="ml-0.5 align-middle"
        />
      );
    };

    const keySafetyNotesCiteBadge = ({
      'data-idx': dataIdx,
    }: {
      'data-idx'?: string;
    }) => {
      const idx = Number(dataIdx);
      const sources = citationMapRef.current?.get(idx);
      if (!sources?.length) return null;
      return (
        <CitationBadge
          sources={sources}
          sessionId={sessionId ?? undefined}
          surfaceBlock="key_safety_notes"
          citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
          opaquePartnerFill
          // The muted card is `bg-background-tertiary`, which is the badge's
          // own default fill — without lifting the badge onto `surface` and
          // giving it a real border it disappears into the block. The certified
          // amber card needs no lift.
          className={cn(
            'ml-0.5 align-middle',
            isKeySafetyNotesMuted && 'border-border bg-surface hover:bg-surface-hover'
          )}
        />
      );
    };

    return {
      ...getEvidenceMdComponents(density),
      'cite-badge': ({ 'data-idx': dataIdx }: { 'data-idx'?: string }) => {
        const idx = Number(dataIdx);
        const sources = citationMapRef.current?.get(idx);
        if (!sources?.length) return null;
        return (
          <CitationBadge
            key={`cite-${idx}-v${partnerConfigVersion ?? 0}`}
            sources={sources}
            sessionId={sessionId ?? undefined}
            surfaceBlock="prose"
            citationNumber={Number.isFinite(idx) ? idx + 1 : undefined}
            className="ml-0.5 align-middle"
          />
        );
      },
      // The child answer has NO legitimate raw image — every image renders via
      // an `img-figure` marker resolved against the server-vetted registry. A
      // raw <img>/markdown image here can only be prompt-grammar violation or
      // search-result injection, and rendering it would fire a request to an
      // unvetted URL (the anti-exfiltration invariant the child prompt relies
      // on). Render nothing, so no element and no fetch ever exist.
      img: () => null,
      'img-figure': ({
        'data-token': dataToken,
        'data-n': dataN,
      }: {
        'data-token'?: string;
        'data-n'?: string;
      }) => {
        const image = dataToken ? imageMapRef.current?.get(dataToken) : undefined;
        if (!image) return null;
        const n = Number(dataN);
        if (image.figure_id) {
          if (!partnerNejmFiguresEnabled) return null;
          return (
            <PartnerFigurePreview
              figure={{
                id: image.figure_id,
                label: image.title,
                caption: image.description,
              }}
            />
          );
        }
        if (!image.url) return null;
        // The legacy chat's block — Tavily inline images are pixel-identical to it.
        return (
          <EvidenceImageBlock
            image={{
              n: Number.isFinite(n) && n > 0 ? n : 1,
              url: image.url,
              description: image.description ?? image.title,
            }}
          />
        );
      },
      // A paragraph hosting an anchor must not render as <p>: the figure (and
      // its caption <p>) are block content, and <figure>-inside-<p> is invalid
      // HTML that React flags as a hydration hazard. Same classes, so prose
      // styling is unchanged.
      p: (props: {
        node?: { children?: { type?: string; tagName?: string }[] };
        children?: ReactNode;
      }) => {
        const hostsFigure = props.node?.children?.some(
          (child) => child.type === 'element' && child.tagName === 'img-figure'
        );
        if (hostsFigure) {
          return (
            <div dir="auto" className={responseParagraphClasses(density)}>
              {props.children}
            </div>
          );
        }
        const BaseParagraph = getEvidenceMdComponents(density).p;
        return <BaseParagraph {...props} />;
      },
      keypoints: ({ children }: { children?: ReactNode }) => (
        <KeyPointsBlock
          isStreaming={streaming}
          citeBadgeComponent={keyPointsCiteBadge}
          density={density}
        >
          {children}
        </KeyPointsBlock>
      ),
      safetycallout: ({ children }: { children?: ReactNode }) => (
        <SafetyCallout
          isStreaming={streaming}
          citeBadgeComponent={safetyCalloutCiteBadge}
        >
          {children}
        </SafetyCallout>
      ),
      keysafetynotes: ({ children }: { children?: ReactNode }) => (
        <KeySafetyNotes
          isStreaming={streaming}
          citeBadgeComponent={keySafetyNotesCiteBadge}
          variant={densityToCalloutVariant(density)}
          density={density}
          muted={isKeySafetyNotesMuted}
        >
          {children}
        </KeySafetyNotes>
      ),
    } as unknown as StreamdownComponents;
  }, [
    citationMapRef,
    imageMapRef,
    streaming,
    density,
    sessionId,
    partnerConfigVersion,
    partnerNejmFiguresEnabled,
    isKeySafetyNotesMuted,
  ]);
};
